
import React, { useState } from 'react';
import { RenovationProject, InspectionItem } from '../../types';
import { ClipboardCheck, Check, Trash2, Plus, Camera, Image as ImageIcon } from 'lucide-react';
import ChecklistEditor from './ChecklistEditor';

interface InspectionChecklistProps {
  project: RenovationProject;
  onUpdate: (project: RenovationProject) => void;
}

const InspectionChecklist: React.FC<InspectionChecklistProps> = ({ project, onUpdate }) => {
  const list = project.inspectionList || [];
  const [editingItem, setEditingItem] = useState<InspectionItem | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const handleSaveItem = (item: InspectionItem) => {
    let updatedList = [...list];
    const existingIndex = updatedList.findIndex(i => i.id === item.id);
    
    if (existingIndex >= 0) {
      updatedList[existingIndex] = item;
    } else {
      updatedList.push(item);
    }
    
    onUpdate({ ...project, inspectionList: updatedList });
  };

  const handleDeleteItem = (id: string) => {
    const updatedList = list.filter(i => i.id !== id);
    onUpdate({ ...project, inspectionList: updatedList });
  };

  const handleToggleItem = (id: string) => {
    const updatedList = list.map(i => i.id === id ? { ...i, isPassed: !i.isPassed } : i);
    onUpdate({ ...project, inspectionList: updatedList });
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (item: InspectionItem) => {
    setEditingItem(item);
    setIsEditorOpen(true);
  };

  const passCount = list.filter(i => i.isPassed).length;
  const isReady = list.length > 0 && passCount === list.length;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Status Header */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-emerald-500" /> Final Inspection
            </h3>
            <p className="text-slate-400 text-sm mt-1">Punch list before final handover.</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase font-bold">Status</p>
            <p className={`text-2xl font-black ${isReady ? 'text-emerald-400' : 'text-amber-400'}`}>
              {isReady ? 'READY FOR HANDOVER' : 'PENDING'}
            </p>
          </div>
        </div>
        
        <div className="w-full bg-slate-800 h-3 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 transition-all duration-500" 
            style={{ width: `${list.length > 0 ? (passCount / list.length) * 100 : 0}%` }}
          ></div>
        </div>
        <p className="text-xs text-slate-500 mt-2 text-right font-mono">{passCount} / {list.length} Items Passed</p>
      </div>

      {/* List Items */}
      <div className="space-y-4">
        {list.map(item => (
          <div 
            key={item.id} 
            className={`bg-slate-950 border p-5 rounded-xl transition-all group relative ${item.isPassed ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-slate-800 hover:border-slate-700'}`}
          >
            <div className="flex items-start gap-4">
              <button 
                onClick={(e) => { e.stopPropagation(); handleToggleItem(item.id); }}
                className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                  item.isPassed 
                  ? 'bg-emerald-500 border-emerald-500 text-white' 
                  : 'border-slate-600 text-transparent hover:border-emerald-500'
                }`}
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              
              <div className="flex-1 cursor-pointer" onClick={() => handleEdit(item)}>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className={`font-bold text-lg ${item.isPassed ? 'text-slate-400 line-through decoration-slate-600' : 'text-white'}`}>
                      {item.description}
                    </h4>
                    <span className="inline-block mt-1 text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded uppercase tracking-wide border border-slate-700">
                      {item.category}
                    </span>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                    className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Photos Preview */}
                {item.photos && item.photos.length > 0 && (
                  <div className="flex gap-2 mt-4">
                    {item.photos.map((photo, idx) => (
                      <div key={idx} className="w-12 h-12 rounded-lg overflow-hidden border border-slate-700">
                        <img src={photo} alt="Evidence" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
                
                {(!item.photos || item.photos.length === 0) && (
                   <div className="flex items-center gap-2 mt-3 text-xs text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-3 h-3" /> Add Photos
                   </div>
                )}
              </div>
            </div>
          </div>
        ))}

        <button 
          onClick={handleAddNew}
          className="w-full py-4 border-2 border-dashed border-slate-800 rounded-xl text-slate-500 hover:border-emerald-500/50 hover:text-emerald-400 hover:bg-slate-900/50 transition-all font-bold flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" /> Add Inspection Item
        </button>
      </div>

      {/* Modal */}
      {isEditorOpen && (
        <ChecklistEditor 
          item={editingItem || undefined}
          onSave={handleSaveItem}
          onClose={() => setIsEditorOpen(false)}
        />
      )}
    </div>
  );
};

export default InspectionChecklist;
